import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users } from 'lucide-react';
import { format } from 'date-fns';

const FeaturedSection = ({ featuredEvents = [], loading = false }) => {
  const navigate = useNavigate();

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  if (loading) {
    return (
      <section className="mb-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-foreground">Featured Events</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)]?.map((_, index) => (
            <div key={index} className="bg-card rounded-lg overflow-hidden shadow-lg animate-pulse">
              <div className="h-48 bg-gray-300"></div>
              <div className="p-6 space-y-4">
                <div className="h-6 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!featuredEvents?.length) {
    return null;
  }

  return (
    <section className="mb-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Featured Events</h2>
          <p className="text-muted-foreground mt-2">
            Don't miss these handpicked premium events
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {featuredEvents?.slice(0, 3)?.map((event) => (
          <div
            key={event?.id}
            onClick={() => handleEventClick(event?.id)}
            className="bg-card rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group border border-border hover:border-primary/50"
          >
            {/* Event Image */}
            <div className="relative h-48 overflow-hidden">
              {event?.image ? (
                <img
                  src={event?.image}
                  alt={event?.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <div className="text-center">
                    <Calendar className="w-12 h-12 text-primary mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Event Image</p>
                  </div>
                </div>
              )}
              
              {/* Featured Badge */}
              <div className="absolute top-3 left-3">
                <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                  Featured
                </span>
              </div>

              {/* Category Badge */}
              <div className="absolute top-3 right-3">
                <span className="bg-black/70 text-white px-3 py-1 rounded-full text-xs capitalize">
                  {event?.category}
                </span>
              </div>
            </div>

            {/* Event Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {event?.title}
              </h3>

              <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                {event?.description || 'Join us for an amazing event experience.'}
              </p>

              {/* Event Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4 mr-2 text-primary" />
                  <span>
                    {event?.date 
                      ? format(new Date(event.date), 'MMM dd, yyyy • h:mm a')
                      : 'Date TBD'
                    }
                  </span>
                </div>

                <div className="flex items-center text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4 mr-2 text-primary" />
                  <span className="line-clamp-1">
                    {event?.location || 'Location TBD'}
                  </span>
                </div>

                <div className="flex items-center text-sm text-muted-foreground">
                  <Users className="w-4 h-4 mr-2 text-primary" />
                  <span>
                    {event?.attendeeCount || 0} attendees
                  </span>
                </div>
              </div>

              {/* Price Range */}
              <div className="flex items-center justify-between">
                <div>
                  {event?.tiers && event?.tiers?.length > 0 ? (
                    <div className="text-sm">
                      <span className="text-muted-foreground">From </span>
                      <span className="text-lg font-bold text-primary">
                        ${Math.min(...event?.tiers?.map(t => t?.price || 0))}
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <span className="text-lg font-bold text-primary">Free</span>
                    </div>
                  )}
                </div>

                <div className="text-xs text-muted-foreground">
                  By {event?.organizer || 'Event Organizer'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View All Featured Button */}
      {featuredEvents?.length > 3 && (
        <div className="text-center mt-8">
          <button
            onClick={() => {
              // Navigate to all events with featured filter
              navigate('/events?featured=true');
            }}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
          >
            View All Featured Events
          </button>
        </div>
      )}
    </section>
  );
};

export default FeaturedSection;